/**
 * Registry for search boxes that are not mounted until the user reveals them.
 *
 * Most search inputs simply carry `data-search-input` and are found by
 * `useSearchHotkey` via a DOM query. A few toolbars keep their search box
 * behind a toggle (advanced mode) — the input does not exist in the DOM at all,
 * so Cmd/Ctrl+F would be a silent no-op there. Those screens register a
 * `reveal` callback here instead; the hotkey handler calls it and re-queries
 * once React has committed.
 *
 * Kept as a module-level singleton (not a context) so `useSearchHotkey` can
 * live at the app root without every screen having to be inside a shared
 * provider.
 */

import { useEffect } from "react";

export type SearchRevealer = {
  /** Flips whatever state mounts the search input (e.g. advanced mode). */
  reveal: () => void;
  /** The container the search box will appear in — used to skip revealers
   *  belonging to a screen that is currently hidden. */
  getNode: () => HTMLElement | null;
};

const revealers = new Set<SearchRevealer>();

/** @returns unregister */
export function registerSearchRevealer(revealer: SearchRevealer): () => void {
  revealers.add(revealer);
  return () => {
    revealers.delete(revealer);
  };
}

export function getSearchRevealers(): SearchRevealer[] {
  return Array.from(revealers);
}

/**
 * Register a reveal callback for the lifetime of the component.
 *
 * `enabled` should be false while the search box IS mounted — a registered
 * revealer makes the hotkey swallow the keystroke, and swallowing it to
 * re-reveal something already visible would be wasted.
 */
export function useSearchReveal(
  reveal: () => void,
  containerRef: React.RefObject<HTMLElement | null>,
  enabled = true,
): void {
  useEffect(() => {
    if (!enabled) return;
    return registerSearchRevealer({
      reveal,
      getNode: () => containerRef.current,
    });
    // `reveal` is re-created per render by callers; re-registering is cheap
    // (Set add/delete) and keeps the closure pointing at fresh state setters.
  }, [reveal, containerRef, enabled]);
}
